import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';

import { AuthProvider } from '@/context/AuthContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { ClientProvider } from '@/context/ClientContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import Layout from '@/components/Layout';

import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import Onboarding from '@/pages/Onboarding';
import Dashboard from '@/pages/Dashboard';
import Assessments from '@/pages/Assessments';
import Competitors from '@/pages/Competitors';
import ContentFactory from '@/pages/ContentFactory';
import Simulator from '@/pages/Simulator';
import AuthorityGraph from '@/pages/AuthorityGraph';
import Marketplace from '@/pages/Marketplace';
import Reports from '@/pages/Reports';
import Agents from '@/pages/Agents';
import Settings from '@/pages/Settings';
import Team from '@/pages/Team';
import Clients from '@/pages/Clients';
import Intake from '@/pages/Intake';

function AppRouter() {
  const location = useLocation();
  // CRITICAL: handle session_id BEFORE rendering normal routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <ClientProvider>
              <Layout><Onboarding /></Layout>
            </ClientProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <ClientProvider>
              <Layout><Dashboard /></Layout>
            </ClientProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assessments"
        element={
          <ProtectedRoute>
            <ClientProvider>
              <Layout><Assessments /></Layout>
            </ClientProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/competitors"
        element={
          <ProtectedRoute>
            <ClientProvider>
              <Layout><Competitors /></Layout>
            </ClientProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/content"
        element={
          <ProtectedRoute>
            <ClientProvider>
              <Layout><ContentFactory /></Layout>
            </ClientProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/simulator"
        element={
          <ProtectedRoute>
            <ClientProvider>
              <Layout><Simulator /></Layout>
            </ClientProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/graph"
        element={
          <ProtectedRoute>
            <ClientProvider>
              <Layout><AuthorityGraph /></Layout>
            </ClientProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketplace"
        element={
          <ProtectedRoute>
            <ClientProvider>
              <Layout><Marketplace /></Layout>
            </ClientProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <ClientProvider>
              <Layout><Reports /></Layout>
            </ClientProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agents"
        element={
          <ProtectedRoute>
            <ClientProvider>
              <Layout><Agents /></Layout>
            </ClientProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <ClientProvider>
              <Layout><Settings /></Layout>
            </ClientProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute>
            <ClientProvider>
              <Layout><Team /></Layout>
            </ClientProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <ClientProvider>
              <Layout><Clients /></Layout>
            </ClientProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/intake/:clientId"
        element={
          <ProtectedRoute>
            <ClientProvider>
              <Layout><Intake /></Layout>
            </ClientProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/intake"
        element={
          <ProtectedRoute>
            <ClientProvider>
              <Layout><Intake /></Layout>
            </ClientProvider>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}
